import { HomepageInstantPayout, HomepageLicense, HomepageOffers, HomepageSecureSpending } from '@shared/icons/system';
import { useTranslations } from 'next-intl';
import type { ComponentProps, ElementType } from 'react';
import { ItemPod } from '../ItemPod/ItemPod';
import { ItemsGrid } from '../ItemsGrid/ItemsGrid';
import { BenefitContainer } from './components/BenefitContainer';
import { benefitItemClasses, type BenefitsSectionDensity } from './styles';

const HOMEPAGE_BENEFIT_ICONS = [HomepageOffers, HomepageInstantPayout, HomepageSecureSpending, HomepageLicense] as const;

type BenefitsSectionProps = ComponentProps<'section'> & {
  namespace: string;
  icons?: readonly [ElementType, ElementType, ElementType, ElementType];
  density?: BenefitsSectionDensity;
};

/**
 * A complete benefits section component that displays a title, description,
 * a list of benefits in a responsive grid (2 columns on desktop, 1 on mobile),
 * and a "Join now" button. All content including benefit items is loaded from translations.
 *
 * @param namespace - Translation namespace to load all content
 * @param icons - Icon components for each benefit, matched to `items` by index. Defaults to the Lottoland icon set.
 * @param className - Additional CSS classes
 * @param density - Spacing preset for the section and the benefit pod surface. Defaults to `default`.
 */
function BenefitsSection({ namespace, icons = HOMEPAGE_BENEFIT_ICONS, className, density, ...props }: BenefitsSectionProps) {
  const t = useTranslations(namespace);
  const benefitTexts = (t.has('items') ? t.raw('items') : []) as string[];

  return (
    <BenefitContainer title={t('title')} namespace={namespace} className={className} density={density} data-testid="benefits-section" {...props}>
      <ItemsGrid
        items={benefitTexts}
        renderItem={(text, index) => {
          const Icon = icons[index] as ElementType;

          return (
            <ItemPod text={text} icon={<Icon size="small" className="text-accent-green-foreground" />} className={benefitItemClasses({ density })} allowTextWrap textClassName="" />
          );
        }}
        data-testid="benefits-grid"
      />
    </BenefitContainer>
  );
}

export { BenefitsSection, type BenefitsSectionProps };
